import * as R from 'ramda';

import {
  AssetElementQuantityInput,
  AssetElementsCreateInput,
} from '@atom/types/asset';
import { SchemaTree, SchemaTreeElementGroup } from '@atom/types/schema';

export const MIN_QUANTITY = 1;
export const MAX_QUANTITY = 99;

export type ElementSelection = Record<string, number>;

// `path` is the list of schema ids from the root of the tree down to a node.
export const pathKey = (path: string[]): string => path.join('/');
export const groupKey = (group: SchemaTreeElementGroup): string =>
  `group:${group.id}`;

export const clampQuantity = (value: string): number => {
  const parsed = Math.floor(Number(value));
  return Number.isNaN(parsed)
    ? MIN_QUANTITY
    : R.clamp(MIN_QUANTITY, MAX_QUANTITY, parsed);
};

export const isSelected = (
  selection: ElementSelection,
  path: string[],
): boolean => R.has(pathKey(path), selection);

export const getQuantity = (
  selection: ElementSelection,
  path: string[],
): number => selection[pathKey(path)] ?? MIN_QUANTITY;

export const setQuantity = (
  selection: ElementSelection,
  path: string[],
  value: string,
): ElementSelection => R.assoc(pathKey(path), clampQuantity(value), selection);

const subtreeKeys = (element: SchemaTree, path: string[]): string[] => [
  pathKey(path),
  ...(element.elements ?? []).flatMap(child =>
    subtreeKeys(child, [...path, child.id]),
  ),
];

export const toggleSelection = (
  selection: ElementSelection,
  element: SchemaTree,
  path: string[],
): ElementSelection =>
  isSelected(selection, path)
    ? R.omit(subtreeKeys(element, path), selection)
    : R.assoc(pathKey(path), MIN_QUANTITY, selection);

const toElementInputs = (
  elements: SchemaTree[],
  selection: ElementSelection,
  parentPath: string[],
): AssetElementQuantityInput[] =>
  elements
    .map(element => ({ element, path: [...parentPath, element.id] }))
    .filter(({ path }) => isSelected(selection, path))
    .map(({ element, path }) => {
      const children = toElementInputs(element.elements ?? [], selection, path);

      return {
        schemaId: element.id,
        quantity: getQuantity(selection, path),
        ...(children.length ? { elements: children } : {}),
      };
    });

export const buildElementsCreateInput = ({
  assetId,
  assetSchemaId,
  schemaTree,
  selection,
}: {
  assetId: string;
  assetSchemaId: string;
  schemaTree: SchemaTree;
  selection: ElementSelection;
}): AssetElementsCreateInput => ({
  assetId,
  assetSchemaId,
  elements: [
    ...toElementInputs(schemaTree.elements ?? [], selection, []),
    ...(schemaTree.elementGroups ?? []).flatMap(group =>
      toElementInputs(group.elements ?? [], selection, []),
    ),
  ],
});
